import { useState } from "react";

const TextField = ({
    type = "text",
    value = "",
    placeholder = "",
    label,
    onChange,
    onFocus,
    onBlur,
    disabled = false,
    error,
    iconLeft,
    iconRight,
    iconRightClick,
    iconSize = "14px",
    width = "100%",
    size = "md",
    className = "",
    style = {},
    inputStyle = {},
    showPasswordToggle = true,
    ...rest
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [revealed, setRevealed] = useState(false);

    // Password fields get a reveal toggle for free, so every one of them
    // behaves the same way without each page wiring up its own eye icon.
    // A caller that already passes its own iconRight keeps it — we never
    // silently replace an explicit icon. Opt out with showPasswordToggle={false}.
    const isPassword = type === "password";
    const usePasswordToggle = isPassword && showPasswordToggle && !iconRight;

    // `type` drives masking, so flipping it to "text" is what reveals the value.
    const effectiveType = isPassword && revealed ? "text" : type;
    const effectiveIconRight = usePasswordToggle
        ? (revealed ? "fa-eye-slash" : "fa-eye")
        : iconRight;
    const effectiveIconRightClick = usePasswordToggle
        ? () => setRevealed((v) => !v)
        : iconRightClick;

    const sizes = {
        sm: {
            height: "32px",
            fontSize: "var(--text-sm)",
            padding: "0 10px",
            iconPadding: "32px",
        },
        md: {
            height: "40px",
            fontSize: "var(--text-base)",
            padding: "0 12px",
            iconPadding: "36px",
        },
        lg: {
            height: "48px",
            fontSize: "var(--text-md)",
            padding: "0 14px",
            iconPadding: "42px",
        },
    };

    const currentSize = sizes[size] || sizes.md;

    const containerStyles = {
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: width,
        height: currentSize.height,
        backgroundColor: disabled ? "var(--bg-tertiary)" : "var(--bg-primary)",
        border: `1px solid ${error ? "var(--error)" : isFocused ? "var(--border-focus)" : "var(--border-primary)"}`,
        borderRadius: "var(--radius-md)",
        transition: "border-color var(--transition-fast)",
        ...style,
    };

    const inputStyles = {
        flex: 1,
        width: "100%",
        height: "100%",
        padding: currentSize.padding,
        paddingLeft: iconLeft ? currentSize.iconPadding : currentSize.padding.split(" ")[1],
        paddingRight: effectiveIconRight ? currentSize.iconPadding : currentSize.padding.split(" ")[1],
        backgroundColor: "transparent",
        border: "none",
        outline: "none",
        color: disabled ? "var(--text-tertiary)" : "var(--text-primary)",
        fontSize: currentSize.fontSize,
        fontFamily: "var(--font-primary)",
        fontWeight: "var(--weight-normal)",
        cursor: disabled ? "not-allowed" : "text",
        ...inputStyle,
    };

    const iconStyles = {
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: disabled ? "var(--text-tertiary)" : "var(--text-secondary)",
        fontSize: iconSize,
        pointerEvents: "none",
    };

    const handleFocus = (e) => {
        setIsFocused(true);
        onFocus?.(e);
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        onBlur?.(e);
    };

    return (
        <div className={className} style={{ width }}>
            {label && (
                <label
                    style={{
                        display: "block",
                        marginBottom: "var(--space-1)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--weight-medium)",
                        color: error ? "var(--error)" : "var(--text-secondary)",
                    }}
                >
                    {label}
                </label>
            )}

            <div style={containerStyles}>
                {iconLeft && (
                    <span style={{ ...iconStyles, left: "12px" }}>
                        <i className={`fa ${iconLeft}`} aria-hidden="true" />
                    </span>
                )}

                <input
                    type={effectiveType}
                    value={value}
                    placeholder={placeholder}
                    onChange={onChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={disabled}
                    style={inputStyles}
                    {...rest}
                />

                {effectiveIconRight && (
                    <span
                        style={{
                            ...iconStyles,
                            right: "12px",
                            pointerEvents: effectiveIconRightClick ? "auto" : "none",
                            cursor: effectiveIconRightClick ? "pointer" : "default",
                        }}
                        onClick={disabled ? undefined : effectiveIconRightClick}
                        // A clickable icon is a control, so make it reachable and
                        // operable from the keyboard rather than mouse-only.
                        role={effectiveIconRightClick ? "button" : undefined}
                        tabIndex={effectiveIconRightClick && !disabled ? 0 : undefined}
                        aria-label={usePasswordToggle
                            ? (revealed ? "Hide password" : "Show password")
                            : undefined}
                        aria-pressed={usePasswordToggle ? revealed : undefined}
                        title={usePasswordToggle
                            ? (revealed ? "Hide password" : "Show password")
                            : undefined}
                        onKeyDown={(e) => {
                            if (!effectiveIconRightClick || disabled) return;
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();   // stop Space scrolling / Enter submitting
                                effectiveIconRightClick(e);
                            }
                        }}
                    >
                        <i className={`fa ${effectiveIconRight}`} aria-hidden="true" />
                    </span>
                )}
            </div>

            {error && typeof error === "string" && (
                <span
                    style={{
                        display: "block",
                        marginTop: "var(--space-1)",
                        fontSize: "var(--text-xs)",
                        color: "var(--error)",
                    }}
                >
                    {error}
                </span>
            )}
        </div>
    );
};

export default TextField;
